/**
 * 产品描述富文本编辑器组件
 *
 * 基于 TipTap 构建的轻量级富文本编辑器，仅提供产品描述所需的基础格式：
 * 加粗、斜体、删除线、标题（H2/H3）、有序/无序列表、引用、链接。
 *
 * 自定义元素：`crm-product-editor`
 *
 * 与 `<crm-rich-editor>`（邮件编辑器）的区别：
 * - 输出语义化 <p> 标签（非邮件专用的 DivParagraph <div>）
 * - 无附件/图片/表格/颜色/字号/签名块等邮件耦合功能
 * - 工具栏仅保留基础格式按钮
 *
 * 事件：dispatch `input` CustomEvent，detail.value 为 HTML 字符串。
 */
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Link } from '@tiptap/extension-link';
import { Placeholder } from '@tiptap/extension-placeholder';
import { BaseElement } from '../base-element.js';

@customElement('crm-product-editor')
export class CrmProductEditor extends BaseElement {
  /** 编辑器 HTML 内容（双向绑定：外部设置或编辑器内部更新） */
  @property({ type: String })
  value = '';

  /** 占位符文本 */
  @property({ type: String })
  placeholder = '输入产品描述...';

  /** 编辑区最小高度（px），翻译场景可设小一些 */
  @property({ type: Number })
  minHeight = 200;

  /** TipTap 编辑器实例 */
  @state()
  private editor: Editor | null = null;

  // ---- 工具栏激活状态 ----
  @state() private isBold = false;
  @state() private isItalic = false;
  @state() private isStrike = false;
  @state() private isH2 = false;
  @state() private isH3 = false;
  @state() private isBulletList = false;
  @state() private isOrderedList = false;
  @state() private isBlockquote = false;
  @state() private isLink = false;

  static styles = css`
    :host { display: block; }
    .toolbar {
      display: flex; flex-wrap: wrap; gap: 2px;
      padding: 6px 8px; border: 1px solid #d9d9e0; border-bottom: none;
      border-radius: 4px 4px 0 0; background: #f8f9fa;
    }
    .toolbar-btn {
      padding: 4px 8px; border: none; background: transparent;
      cursor: pointer; border-radius: 3px; font-size: 14px;
      color: #1c2024; min-width: 28px; min-height: 28px;
      display: inline-flex; align-items: center; justify-content: center;
    }
    .toolbar-btn:hover { background: #e8e8ed; }
    .toolbar-btn.is-active { background: #0d74ce; color: #fff; }
    .toolbar-sep { width: 1px; background: #d9d9e0; margin: 2px 4px; }
    .editor {
      padding: 10px 12px; max-height: 420px;
      overflow-y: auto; border: 1px solid #d9d9e0; border-radius: 0 0 4px 4px;
      outline: none; font-size: 14px; line-height: 1.6; color: #1c2024;
    }
    .editor:focus { border-color: #0d74ce; }
    .editor :is(p, h2, h3, ul, ol, blockquote) { margin: 0 0 8px; }
    .editor :is(ul, ol) { padding-left: 20px; }
    .editor h2 { font-size: 18px; font-weight: 600; }
    .editor h3 { font-size: 16px; font-weight: 600; }
    .editor blockquote {
      border-left: 3px solid #d9d9e0; padding-left: 12px; color: #60646c;
    }
    .editor a { color: #0d74ce; }
    .ProseMirror p.is-empty:first-child::before {
      content: attr(data-placeholder); color: #9ca3af;
      float: left; pointer-events: none; height: 0;
    }
  `;

  firstUpdated() {
    const el = this.renderRoot.querySelector('.editor');
    if (!el) return;
    this.editor = new Editor({
      element: el as HTMLElement,
      extensions: [
        StarterKit.configure({
          heading: { levels: [2, 3] },
        }),
        Link.configure({ openOnClick: false, autolink: true }),
        Placeholder.configure({ placeholder: this.placeholder }),
      ],
      content: this.value || '',
      onUpdate: ({ editor }: { editor: Editor }) => {
        const html = editor.getHTML();
        this.dispatchEvent(new CustomEvent('input', {
          detail: { value: html }, bubbles: true, composed: true,
        }));
      },
      onSelectionUpdate: ({ editor }: { editor: Editor }) => {
        this.isBold = editor.isActive('bold');
        this.isItalic = editor.isActive('italic');
        this.isStrike = editor.isActive('strike');
        this.isH2 = editor.isActive('heading', { level: 2 });
        this.isH3 = editor.isActive('heading', { level: 3 });
        this.isBulletList = editor.isActive('bulletList');
        this.isOrderedList = editor.isActive('orderedList');
        this.isBlockquote = editor.isActive('blockquote');
        this.isLink = editor.isActive('link');
      },
    });
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('value') && this.editor) {
      const current = this.editor.getHTML();
      if (this.value !== current) {
        this.editor.commands.setContent(this.value || '', { emitUpdate: false });
      }
    }
  }

  disconnectedCallback() {
    this.editor?.destroy();
    this.editor = null;
    super.disconnectedCallback();
  }

  /** 弹窗设置/移除链接 */
  private setLink() {
    if (!this.editor) return;
    const prev = this.editor.getAttributes('link')['href'] as string | undefined;
    const url = window.prompt('链接 URL', prev || 'https://');
    if (url === null) return;
    if (url === '') {
      this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }

  render() {
    return html`
      <div class="toolbar">
        <button class="toolbar-btn ${this.isBold ? 'is-active' : ''}"
          @click=${() => this.editor?.chain().focus().toggleBold().run()} title="加粗"><b>B</b></button>
        <button class="toolbar-btn ${this.isItalic ? 'is-active' : ''}"
          @click=${() => this.editor?.chain().focus().toggleItalic().run()} title="斜体"><i>I</i></button>
        <button class="toolbar-btn ${this.isStrike ? 'is-active' : ''}"
          @click=${() => this.editor?.chain().focus().toggleStrike().run()} title="删除线"><s>S</s></button>
        <div class="toolbar-sep"></div>
        <button class="toolbar-btn ${this.isH2 ? 'is-active' : ''}"
          @click=${() => this.editor?.chain().focus().toggleHeading({ level: 2 }).run()} title="标题2">H2</button>
        <button class="toolbar-btn ${this.isH3 ? 'is-active' : ''}"
          @click=${() => this.editor?.chain().focus().toggleHeading({ level: 3 }).run()} title="标题3">H3</button>
        <div class="toolbar-sep"></div>
        <button class="toolbar-btn ${this.isBulletList ? 'is-active' : ''}"
          @click=${() => this.editor?.chain().focus().toggleBulletList().run()} title="无序列表">&bull;</button>
        <button class="toolbar-btn ${this.isOrderedList ? 'is-active' : ''}"
          @click=${() => this.editor?.chain().focus().toggleOrderedList().run()} title="有序列表">1.</button>
        <button class="toolbar-btn ${this.isBlockquote ? 'is-active' : ''}"
          @click=${() => this.editor?.chain().focus().toggleBlockquote().run()} title="引用">&ldquo;</button>
        <div class="toolbar-sep"></div>
        <button class="toolbar-btn ${this.isLink ? 'is-active' : ''}"
          @click=${this.setLink} title="插入链接">&#128279;</button>
      </div>
      <div class="editor" style="min-height:${this.minHeight}px"></div>
    `;
  }
}
